import {
  CarePlanStepStatus,
  type MessageTemplateType,
  type PatientCarePlanStep,
  StepType,
} from "@/components/care-plan/types";

import {
  type CarePlanStepSequenceDetail,
  type SaveCarePlanSequencePayload,
} from "@/types/care-plan-sequence";
import { type CarePlanTagDetail } from "@/types/care-plan-template";
import { type TaskPriority } from "@/types/task";

export { CarePlanStepStatus };

// ============================================================================
// Cycle types
// ============================================================================

export enum CycleModificationMode {
  THIS = "THIS",
  FUTURE = "FUTURE",
  ALL = "ALL",
}

export interface CarePlanCycleDetail {
  id: string;
  carePlanId: string;
  branchId: string | null;
  templateCycleId: string | null;
  parentCycleId: string | null;
  name: string | null;
  passCount: number;
  gapMinutes: number;
  position: number;
  createdDate: string;
  updatedDate: string | null;
}

export interface SaveCarePlanCyclePayload {
  id?: string | null;
  branchId?: string | null;
  templateCycleId?: string | null;
  parentCycleId?: string | null;
  name?: string | null;
  passCount: number;
  gapMinutes: number;
  position: number;
  fromStepId?: string;
  toStepId?: string;
}

export interface BranchSelectionPayload {
  stepId: string;
  branchId: string;
}

// ============================================================================
// Step measurement
// ============================================================================

export interface CarePlanStepMeasurement {
  id?: string;
  measurementField: string;
  displayUnit?: string;
}

// ============================================================================
// Branch detail
// ============================================================================

export interface CarePlanBranchDetail {
  id: string;
  decisionStepId: string;
  name?: string;
  position: number;
  tagIds: string[];
  selectedDate?: string;
  selectedById?: string;
  steps: CarePlanStepDetail[];
}

// ============================================================================
// Step detail
// ============================================================================

export interface CarePlanStepDetail {
  id: string;
  carePlanId: string;
  branchId?: string;
  stepNumber: number;
  type: StepType;
  messageType?: MessageTemplateType;
  description?: string;
  minutesAfterPrevious?: number;
  parentStepId?: string;
  sourceServiceId?: string;
  serviceIds: string[];
  medicationIds: string[];
  measurements: CarePlanStepMeasurement[];
  smsBody?: string;
  taskTitle?: string;
  taskPriority?: TaskPriority;
  taskAssigneeId?: string;
  taskTeamId?: string;
  taskDescription?: string;
  taskDueDateMinutes?: number;
  // Care plan specific
  scheduledDate: string;
  status: CarePlanStepStatus;
  eventId?: string;
  taskId?: string;
  completedDate?: string;
  isDateEstimated?: boolean;
  branches?: CarePlanBranchDetail[];
  // Cycle fields
  cycleId: string | null;
  cyclePass: number | null;
  sourceStepId: string | null;
  sequences?: CarePlanStepSequenceDetail[];
}

// ============================================================================
// Summary (list view)
// ============================================================================

export interface CarePlanSummary {
  id: string;
  name: string;
  description?: string;
  patientId: string;
  locationId: string;
  assignedById: string;
  templateId?: string;
  startDate: string;
  stepCount: number;
  completedStepCount: number;
  tagIds: string[];
  tags: CarePlanTagDetail[];
  createdDate: string;
  updatedDate?: string;
}

// ============================================================================
// Full detail
// ============================================================================

export interface CarePlanDetail extends CarePlanSummary {
  steps: CarePlanStepDetail[];
  cycles: CarePlanCycleDetail[];
}

// ============================================================================
// Create / Update payloads
// ============================================================================

export interface CreateCarePlanPayload {
  name: string;
  description?: string;
  patientId: string;
  locationId: string;
  startDate: string;
  templateId?: string;
  tagIds: string[];
  steps: SaveCarePlanStepPayload[];
  cycles?: SaveCarePlanCyclePayload[];
}

export interface UpdateCarePlanPayload {
  name?: string;
  description?: string;
  startDate?: string;
  tagIds?: string[];
}

// ============================================================================
// Step save payloads
// ============================================================================

export interface SaveCarePlanStepMeasurement {
  measurementField: string;
  displayUnit?: string;
}

export interface SaveCarePlanBranchPayload {
  id?: string;
  templateBranchId?: string;
  name?: string;
  position: number;
  tagIds: string[];
  steps: SaveCarePlanStepPayload[];
}

export interface SaveCarePlanStepPayload {
  id?: string;
  templateStepId?: string;
  type: StepType;
  messageType?: MessageTemplateType;
  description?: string;
  minutesAfterPrevious?: number;
  parentStepId?: string;
  sourceServiceId?: string;
  serviceIds: string[];
  medicationIds: string[];
  measurements: SaveCarePlanStepMeasurement[];
  smsBody?: string;
  taskTitle?: string;
  taskPriority?: TaskPriority;
  taskAssigneeId?: string;
  taskTeamId?: string;
  taskDescription?: string;
  taskDueDateMinutes?: number;
  scheduledDate: string;
  status?: CarePlanStepStatus;
  eventId?: string;
  taskId?: string;
  branches?: SaveCarePlanBranchPayload[];
  // Cycle fields
  cycleId?: string | null;
  cyclePass?: number | null;
  /**
   * Step-level sequences to reconcile against persisted state.
   *
   * Sentinel semantics (`CarePlanStepSave.sequences` on the backend):
   *   - `undefined` ⇒ leave alone
   *   - `[]` ⇒ archive every step-level sequence
   *   - `[ ... ]` ⇒ reconcile against persisted state
   */
  sequences?: SaveCarePlanSequencePayload[];
}

// ============================================================================
// Step serialization
// ============================================================================

export function toStepPayload(
  step: PatientCarePlanStep,
): SaveCarePlanStepPayload {
  return {
    id: step.id,
    templateStepId: step.templateStepId,
    type: step.type,
    messageType: step.messageType,
    description: step.description || undefined,
    minutesAfterPrevious: step.minutesAfterPrevious,
    parentStepId: step.parentStepId,
    sourceServiceId: step.sourceServiceId,
    serviceIds: step.serviceIds,
    medicationIds: step.medicationIds,
    measurements: step.measurements.map((m) => ({
      measurementField: m.measurementField,
      displayUnit: m.displayUnit,
    })),
    smsBody: step.type === StepType.MESSAGE ? step.smsBody : undefined,
    taskTitle: step.taskTitle,
    taskPriority: step.taskPriority,
    taskAssigneeId: step.taskAssigneeId,
    taskTeamId: step.taskTeamId,
    taskDescription: step.taskDescription,
    taskDueDateMinutes: step.taskDueDateMinutes,
    scheduledDate: step.scheduledDate,
    status: step.status,
    eventId: step.eventId,
    taskId: step.taskId,
    cycleId: step.cycleId,
    cyclePass: step.cyclePass,
    branches: step.branches?.map((b) => ({
      id: b.id,
      templateBranchId: b.templateBranchId,
      name: b.name,
      position: b.position,
      tagIds: b.tagIds,
      steps: (b.steps as PatientCarePlanStep[]).map(toStepPayload),
    })),
    sequences: step.sequencesDirty
      ? toSequenceSavePayload(step.sequences ?? [])
      : undefined,
  };
}

// Sort by delay ascending so display_order matches firing order on save.
export function toSequenceSavePayload(
  drafts: NonNullable<PatientCarePlanStep["sequences"]>,
): SaveCarePlanSequencePayload[] {
  if (drafts.length === 0) return [];
  const sorted = [...drafts].sort((a, b) => a.delayMinutes - b.delayMinutes);
  return [
    {
      isActive: true,
      steps: sorted.map((d, i) => ({
        id: d.id,
        triggerEvent: "CARE_PLAN_STEP_PENDING",
        delayMinutes: d.delayMinutes,
        effectType: d.effectType,
        config: buildEffectConfig(d),
        displayOrder: i,
        isActive: d.isActive,
      })),
    },
  ];
}

function buildEffectConfig(
  d: NonNullable<PatientCarePlanStep["sequences"]>[number],
): Record<string, unknown> {
  if (d.effectType === "SEND_SMS") {
    return { effect: { template: d.smsBody ?? "" } };
  }
  return {
    effect: {
      template: d.emailBody ?? "",
      subject: d.emailSubject ?? "",
      template_json: d.emailTemplateJson,
    },
  };
}

// ============================================================================
// Step status update
// ============================================================================

export interface CarePlanStepStatusPayload {
  status: CarePlanStepStatus;
}
